<template>
  <v-card
    class="sort-and-filter"
    :data-cy="`${cyTag}-filter-menu`"
  >
    <v-list
      v-if="showMenu"
      class="sort-and-filter__menu"
      subheader
    >
      <sort-options
        v-if="showSort"
        :sort-by-options="sortByOptions"
        :active-sort="activeSort"
        @change="selectedSort = $event"
      />
      <filter-options
        v-if="showFilters"
        :filter-options="unsavedFilterChanges"
        :hide-clear-all="hideClearAllFilters"
        :hide-search="hideFilterSearch"
        :max-height="showSort ? '250px' : '400px'"
        :single-select="filterOptions.singleSelect"
        :dynamic="filterOptions.dynamic"
        :has-next-page="hasNextPage"
        @filters:change="updateUnsavedChanges"
        @filters:search="$emit('filters:search', $event)"
        @filters:clear="clearFilters"
        @filters:load-more="$emit('filters:load-more')"
      />
    </v-list>
    <v-card-actions class="pa-0">
      <v-spacer />
      <sn-btn
        v-if="showMenu"
        size="standard"
        text="Apply"
        class="sort-and-filter__apply"
        data-cy="apply-btn"
        @click="applyChanges"
      />
    </v-card-actions>
  </v-card>
</template>

<script>
import SortOptions from './sortOptions.vue'
import FilterOptions from './filterOptions.vue'
import SnBtn from '~/components/snui/v2/snBtn/snBtn'
import { cloneDeep, isEqual } from 'lodash'

export default {
  name: 'SortAndFilterMenu',
  components: {
    SortOptions,
    FilterOptions,
    SnBtn
  },
  props: {
    sortByOptions: {
      type: Array,
      required: false,
      default: () => []
    },
    hideSort: {
      type: Boolean,
      required: false,
      default: false
    },
    activeSort: {
      type: String,
      required: false,
      default: ''
    },

    filterOptions: {
      type: [Array, Object],
      required: false,
      default: null
    },
    hideFilters: {
      type: Boolean,
      required: false,
      default: false
    },
    hideClearAllFilters: {
      type: Boolean,
      required: false,
      default: false
    },
    hideFilterSearch: {
      type: Boolean,
      required: false,
      default: false
    },

    cyTag: {
      type: String,
      required: false,
      default: ''
    }
  },
  data () {
    return {
      selectedSort: null,
      unsavedFilterChanges: [],
      menuOpen: false
    }
  },
  computed: {
    hasNextPage () {
      return this.filterOptions?.pageInfo?.hasNextPage ?? false
    },
    showSort () {
      return this.sortByOptions?.length && !this.hideSort
    },
    showFilters () {
      return this.filterOptions && !this.hideFilters
    },
    showMenu () {
      return this.showSort || this.showFilters
    },
    rawFilters () {
      return this.filterOptions?.nodes ?? this.filterOptions
    }
  },
  watch: {
    filterOptions: {
      deep: true,
      immediate: true,
      handler (filterOptions) {
        this.unsavedFilterChanges = cloneDeep(filterOptions?.nodes ?? filterOptions)
      }
    }
  },
  methods: {
    applyChanges () {
      const areFilterChanges = !isEqual(this.rawFilters, this.unsavedFilterChanges)
      let filters = null

      if (areFilterChanges) {
        if (this.filterOptions?.nodes != null) {
          filters = {
            ...this.filterOptions,
            nodes: this.unsavedFilterChanges
          }
        } else {
          filters = this.unsavedFilterChanges
        }
      }

      this.$emit('filters:change', {
        sortBy: this.selectedSort,
        filters
      })
    },
    clearFilters () {
      this.updateUnsavedChanges()
      this.applyChanges()
    },
    updateUnsavedChanges (changedFilterValue = null) {
      this.unsavedFilterChanges = this.unsavedFilterChanges.map(f => {
        const singleSelect = this.filterOptions.singleSelect

        if (changedFilterValue === null || (singleSelect && f.value !== changedFilterValue)) {
          f.active = false
        } else if (f.value === changedFilterValue) {
          f.active = !f.active
        }

        return f
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';

.sort-and-filter {
  &__menu {
    width: 250px;
  }

  &__apply {
    float: right;
    margin: 15px;
  }
}
</style>
